<template>
  <div class="sched-logs">
    <APageHead title="스케줄러 로그 관리" sub="스케줄 작업을 실시간으로 모니터링하고 관리하세요.">
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="fetchStatistics">새로고침</ABtn>
        <ABtn kind="danger" icon="delete_sweep" @click="showCleanupDialog">로그 정리</ABtn>
      </template>
    </APageHead>

    <div v-if="statistics" class="sched-logs__stats">
      <ACard>
        <div class="sched-logs__stat-row">
          <AStat label="총 작업" :value="String(statistics.total_logs || 0)" />
          <div class="sched-logs__stat-icon sched-logs__stat-icon--info"><i class="material-icons-round">format_list_numbered</i></div>
        </div>
      </ACard>
      <ACard>
        <div class="sched-logs__stat-row">
          <AStat label="성공" :value="String(statistics.success_count || 0)" />
          <div class="sched-logs__stat-icon sched-logs__stat-icon--ok"><i class="material-icons-round">check_circle</i></div>
        </div>
      </ACard>
      <ACard>
        <div class="sched-logs__stat-row">
          <AStat label="실패" :value="String(statistics.failed_count || 0)" />
          <div class="sched-logs__stat-icon sched-logs__stat-icon--err"><i class="material-icons-round">error</i></div>
        </div>
      </ACard>
      <ACard>
        <div class="sched-logs__stat-row">
          <AStat label="성공률" :value="calculateSuccessRate()" unit="%" />
          <div class="sched-logs__stat-icon sched-logs__stat-icon--warn"><i class="material-icons-round">trending_up</i></div>
        </div>
      </ACard>
    </div>

    <div
      v-if="statistics && statistics.recent_failures && statistics.recent_failures.length > 0"
      class="sched-logs__failure-alert"
    >
      <div class="sched-logs__failure-head">
        <i class="material-icons-round">warning</i>
        <h5>최근 실패 작업</h5>
      </div>
      <div v-for="(failure, index) in statistics.recent_failures" :key="index" class="sched-logs__failure-row">
        <div>
          <strong>{{ failure.job_name }}</strong>
          <span class="sched-logs__failure-time">{{ formatDateTime(failure.timestamp) }}</span>
        </div>
        <div class="sched-logs__failure-err">{{ failure.error }}</div>
      </div>
    </div>

    <ACard>
      <div class="sched-logs__filters">
        <div class="p-field sched-logs__filter sched-logs__filter--sm">
          <label class="p-field__label">상태</label>
          <select v-model="filters.status" class="p-input">
            <option value="">전체 상태</option>
            <option value="success">성공</option>
            <option value="failed">실패</option>
            <option value="running">실행중</option>
            <option value="submitted">대기중</option>
          </select>
        </div>
        <div class="p-field sched-logs__filter sched-logs__filter--grow">
          <label class="p-field__label">작업명</label>
          <input v-model="filters.job_name" class="p-input" placeholder="작업명 검색" @keyup.enter="resetPage" />
        </div>
        <div class="p-field sched-logs__filter sched-logs__filter--sm">
          <label class="p-field__label">기간</label>
          <select v-model="filters.days" class="p-input">
            <option value="7">최근 7일</option>
            <option value="14">최근 14일</option>
            <option value="30">최근 30일</option>
            <option value="60">최근 60일</option>
            <option value="90">최근 90일</option>
          </select>
        </div>
        <div class="p-field sched-logs__filter sched-logs__filter--sm">
          <label class="p-field__label">개수</label>
          <select v-model.number="filters.limit" class="p-input" @change="resetPage">
            <option :value="10">10개</option>
            <option :value="20">20개</option>
            <option :value="50">50개</option>
            <option :value="100">100개</option>
            <option :value="200">200개</option>
          </select>
        </div>
        <div class="sched-logs__filter-btn">
          <ABtn kind="yellow" icon="search" @click="resetPage">검색</ABtn>
        </div>
      </div>
    </ACard>

    <ACard body-flush>
      <AEmpty v-if="!loading && logs.length === 0" title="데이터가 없습니다." />

      <ATable
        v-else
        :columns="cols"
        :rows="logs"
        row-key="_id"
      >
        <template #cell:timestamp="{ row }">
          <span class="sched-logs__num">{{ formatDateTime(row.timestamp) }}</span>
        </template>
        <template #cell:job_name="{ row }">
          <strong>{{ row.job_name }}</strong>
        </template>
        <template #cell:task_name="{ row }">{{ row.task_name || '-' }}</template>
        <template #cell:status="{ row }">
          <APill :kind="statusPillKind(row.status)">{{ getStatusLabel(row.status) }}</APill>
        </template>
        <template #cell:duration="{ row }">
          <span class="sched-logs__num">
            {{ row.details && row.details.duration_seconds ? `${row.details.duration_seconds.toFixed(2)}초` : '-' }}
          </span>
        </template>
        <template #cell:actions="{ row }">
          <ABtn kind="ghost" size="sm" icon="visibility" icon-only @click="showDetails(row)" />
        </template>
      </ATable>

      <div v-if="showPager" class="sched-logs__pager">
        <ABtn
          kind="ghost"
          size="sm"
          icon="chevron_left"
          icon-only
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        />
        <button
          v-for="page in displayPages"
          :key="page"
          type="button"
          class="sched-logs__pager-num"
          :class="{ 'is-active': page === currentPage }"
          @click="changePage(page)"
        >{{ page }}</button>
        <ABtn
          kind="ghost"
          size="sm"
          icon="chevron_right"
          icon-only
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        />
      </div>
    </ACard>

    <!-- 상세 모달 -->
    <AModal v-model="detailOpen" title="스케줄러 로그 상세" size="lg">
      <div v-if="selectedLog" class="sched-logs__detail-grid">
        <div>
          <h6 class="sched-logs__detail-h">기본 정보</h6>
          <table class="sched-logs__detail-tbl">
            <tr><th>작업 ID</th><td>{{ selectedLog.job_id }}</td></tr>
            <tr><th>작업명</th><td>{{ selectedLog.job_name }}</td></tr>
            <tr><th>태스크명</th><td>{{ selectedLog.task_name || '-' }}</td></tr>
            <tr><th>이벤트 타입</th><td>{{ selectedLog.event_type }}</td></tr>
            <tr>
              <th>상태</th>
              <td><APill :kind="statusPillKind(selectedLog.status)">{{ getStatusLabel(selectedLog.status) }}</APill></td>
            </tr>
            <tr><th>타임스탬프</th><td>{{ formatDateTime(selectedLog.timestamp) }}</td></tr>
          </table>
        </div>
        <div v-if="selectedLog.details">
          <h6 class="sched-logs__detail-h">상세 정보</h6>
          <table class="sched-logs__detail-tbl">
            <tr v-if="selectedLog.details.execution_time">
              <th>실행 시간</th><td>{{ formatDateTime(selectedLog.details.execution_time) }}</td>
            </tr>
            <tr v-if="selectedLog.details.scheduled_run_time">
              <th>예정 시간</th><td>{{ formatDateTime(selectedLog.details.scheduled_run_time) }}</td>
            </tr>
            <tr v-if="selectedLog.details.duration_seconds">
              <th>소요 시간</th><td>{{ selectedLog.details.duration_seconds.toFixed(2) }}초</td>
            </tr>
          </table>
          <div v-if="selectedLog.details.error" class="sched-logs__alert sched-logs__alert--err">
            <strong>에러:</strong> {{ selectedLog.details.error }}
          </div>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="detailOpen = false">닫기</ABtn>
      </template>
    </AModal>

    <!-- 정리 모달 -->
    <AModal v-model="cleanupOpen" title="오래된 로그 정리" size="sm">
      <p class="sched-logs__cleanup-d">며칠 이전의 로그를 삭제하시겠습니까?</p>
      <div class="p-field">
        <label class="p-field__label">보관 기간 (일)</label>
        <select v-model="cleanupDays" class="p-input">
          <option value="7">7일 이전</option>
          <option value="14">14일 이전</option>
          <option value="30">30일 이전</option>
          <option value="60">60일 이전</option>
          <option value="90">90일 이전</option>
        </select>
      </div>
      <div class="sched-logs__alert sched-logs__alert--warn">
        <i class="material-icons-round">warning</i>
        선택한 기간 이전의 모든 스케줄러 로그가 영구적으로 삭제됩니다.
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="cleanupOpen = false">취소</ABtn>
        <ABtn kind="danger-fill" icon="delete" @click="performCleanup">삭제 실행</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import { formatServerClock } from "@/utils/datetime";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "SystemLogsSchedules",
  components: { APageHead, ACard, ABtn, APill, AStat, ATable, AModal, AEmpty },
  data() {
    return {
      logs: [],
      statistics: null,
      loading: false,
      filters: {
        status: '',
        job_name: '',
        task_name: '',
        days: 7,
        limit: 20,
        skip: 0,
      },
      currentPage: 1,
      hasMore: false,
      selectedLog: null,
      cleanupDays: 30,
      detailOpen: false,
      cleanupOpen: false,
      cols: [
        { key: 'timestamp', label: '시간',     width: 170 },
        { key: 'job_name',  label: '작업명' },
        { key: 'task_name', label: '태스크' },
        { key: 'event_type', label: '이벤트', width: 110 },
        { key: 'status',    label: '상태', width: 100, align: 'center' },
        { key: 'duration',  label: '실행시간', width: 110 },
        { key: 'actions',   label: '작업', width: 70, align: 'center' },
      ],
    };
  },
  computed: {
    totalPages() {
      return this.currentPage + (this.hasMore ? 1 : 0);
    },
    displayPages() {
      const pages = [];
      const maxDisplay = 5;
      const half = Math.floor(maxDisplay / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxDisplay - 1);
      if (end - start < maxDisplay - 1) start = Math.max(1, end - maxDisplay + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    showPager() {
      return this.currentPage > 1 || this.hasMore;
    },
  },
  mounted() {
    this.fetchStatistics();
    this.fetchLogs();
  },
  methods: {
    async fetchStatistics() {
      try {
        const response = await this.$axios({
          method: "get",
          url: "/system/logs/scheduler/stats",
          params: { days: this.filters.days },
        });
        this.statistics = response.data;
      } catch (error) {
        console.error("통계 조회 실패:", error);
        this.$notifyVue({ type: "error", title: "통계를 불러오는데 실패했습니다." });
      }
    },

    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          days: this.filters.days,
          limit: this.filters.limit,
          skip: this.filters.skip,
        };
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.job_name) params.job_name = this.filters.job_name;
        if (this.filters.task_name) params.task_name = this.filters.task_name;

        const response = await this.$axios({
          method: "get",
          url: "/system/logs/scheduler",
          params,
          loadingBar: true,
        });
        this.logs = response.data || [];
        this.hasMore = this.logs.length >= this.filters.limit;
        await this.fetchStatistics();
      } catch (error) {
        console.error("로그 조회 실패:", error);
        this.$notifyVue({ type: "error", title: "로그를 불러오는데 실패했습니다." });
        this.logs = [];
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      if (page < 1) return;
      if (page > this.currentPage && !this.hasMore) return;
      this.currentPage = page;
      this.filters.skip = (page - 1) * this.filters.limit;
      this.fetchLogs();
    },

    resetPage() {
      this.currentPage = 1;
      this.filters.skip = 0;
      this.fetchLogs();
    },

    showDetails(log) {
      this.selectedLog = log;
      this.detailOpen = true;
    },

    showCleanupDialog() {
      this.cleanupOpen = true;
    },

    async performCleanup() {
      try {
        const response = await this.$axios({
          method: "delete",
          url: "/system/logs/scheduler/cleanup",
          params: { days: this.cleanupDays },
          loadingBar: true,
        });
        this.$notifyVue({ type: "success", title: `${response.data.deleted_count}개의 로그가 삭제되었습니다.` });
        this.cleanupOpen = false;
        this.fetchLogs();
      } catch (error) {
        console.error("로그 정리 실패:", error);
        this.$notifyVue({ type: "error", title: "로그 정리에 실패했습니다." });
      }
    },

    // 스케줄러 로그 시각은 UTC(+00:00 표기)로 바뀌었다
    // (docs/시각표기_시간대_규칙.md §4).
    // 관리자가 해외에서 접속해도 같은 값이 보이도록 브라우저 로컬이 아닌 KST 로 고정한다.
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return formatServerClock(dateString) || '-';
    },

    statusPillKind(status) {
      const map = {
        'success': 'ok', 'failed': 'err', 'running': 'warn',
        'submitted': 'info', 'executed': 'ok',
      };
      return map[status] || 'default';
    },

    getStatusLabel(status) {
      const labels = {
        'success': '성공', 'failed': '실패', 'running': '실행중',
        'submitted': '대기중', 'executed': '완료',
      };
      return labels[status] || status;
    },

    calculateSuccessRate() {
      if (!this.statistics) return '0.00';
      const success = this.statistics.success_count || 0;
      const failed = this.statistics.failed_count || 0;
      const completed = success + failed;
      if (completed === 0) return '0.00';
      return ((success / completed) * 100).toFixed(2);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.sched-logs {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 576px) { grid-template-columns: 1fr; }
  }

  &__stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__stat-icon {
    width: 44px;
    height: 44px;
    border-radius: $p-radius-md;
    display: grid;
    place-items: center;

    > i { font-size: 22px; }

    &--info { background: $p-info-soft; color: $p-info; }
    &--ok   { background: $p-ok-soft;   color: $p-ok; }
    &--err  { background: $p-err-soft;  color: $p-err; }
    &--warn { background: $p-warn-soft; color: $p-warn; }
  }

  &__failure-alert {
    background: $p-err-soft;
    border-left: 3px solid $p-err;
    padding: 14px 18px;
    border-radius: $p-radius-md;
    color: $p-err;
  }

  &__failure-head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;

    h5 { margin: 0; font-size: 13px; font-weight: 800; }
    > i { font-size: 18px; }
  }

  &__failure-row {
    margin-bottom: 8px;
    font-size: 12px;

    &:last-child { margin-bottom: 0; }
  }

  &__failure-time {
    margin-left: 8px;
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__failure-err {
    margin-top: 2px;
    font-size: 11px;
    color: $p-err;
    opacity: 0.85;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
  }

  &__filter {
    flex: 0 0 auto;

    &--sm { width: 130px; }
    &--md { width: 160px; }
    &--grow {
      flex: 1 1 220px;
      min-width: 180px;
    }
  }

  &__filter-btn {
    display: flex;
    align-items: flex-end;
    padding-bottom: 1px;
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
  }

  &__pager {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 14px;
  }

  &__pager-num {
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    border-radius: $p-radius;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }

    &.is-active {
      background: $p-point-deep;
      border-color: $p-point-deep;
      color: $p-panel;
    }
  }

  // 모달 상세
  &__detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) { grid-template-columns: 1fr; }
  }

  &__detail-h {
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 800;
    color: $p-ink2;
  }

  &__detail-tbl {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 8px 12px;
      border: 1px solid $p-line;
      text-align: left;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
      width: 40%;
    }
  }

  &__alert {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;
    margin-top: 12px;

    > i { font-size: 18px; }

    &--err  { background: $p-err-soft;  border-left: 3px solid $p-err;  color: $p-err; }
    &--warn { background: $p-warn-soft; border-left: 3px solid $p-warn; color: $p-warn; }
  }

  &__cleanup-d {
    margin: 0 0 14px;
    font-size: 13px;
    color: $p-ink;
  }
}
</style>
